import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { LogoLoading } from "./utlity/utility";
import { PLAN_LEVELS, PLAN_MAPPING } from "../../constants/planLevels";
import { FiLock, FiArrowLeft } from "react-icons/fi";

const ProtectedRoute = ({
  children,
  routeKey,
  requiredPermissions,
  requiredLevel,
  fallbackPath,
}) => {
  const navigate = useNavigate();
  const { bussinessUuid } = useParams();
  const { allowedRoutes, isOwnerAdmin, permissions, loading } = useSelector(
    (state) => state.rolePermission
  );
  const user = useSelector((state) => state.user.user);
  const businessData = useSelector((state) => state.bussiness?.business);

  // Find the current business to check its subscription level
  const currentBusiness = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid)
    : (businessData?.bussinessUuid === bussinessUuid ? businessData : null);

  // Plan inheritance: Business level takes high priority for context-based access
  const rawBusinessPlan = currentBusiness?.subscription?.toUpperCase();
  const rawUserPlan = (user?.subscription || "FREE").toUpperCase();

  // Handle mapping from legacy plans (GOLD, BRONZE, etc.) to new tiers (BASIC, GROWTH, PRO)
  const businessPlan = PLAN_MAPPING[rawBusinessPlan] || rawBusinessPlan;
  const userPlan = PLAN_MAPPING[rawUserPlan] || rawUserPlan;

  // Primary source of truth is the active business when in business context, fallback to user account only for base routes
  const currentPlan = bussinessUuid ? (businessPlan || "FREE") : (userPlan || "FREE");
  const currentEndDate = currentBusiness?.subscriptionEndDate || user?.subscriptionEndDate;
  const isExpired = currentEndDate && new Date(currentEndDate) < new Date();

  // If the plan is expired, treat the level as 0 (FREE), otherwise look up the level
  const currentLevel = isExpired ? 0 : (PLAN_LEVELS[currentPlan] || 0);


  // Plan-based access check
  const isEssentialRoute = routeKey === 'subscription' || routeKey === 'support' || routeKey === 'dashboard';
  const hasPlanAccess = !requiredLevel || currentLevel >= requiredLevel;

  // Mandatory subscription/trial check for new or expired users
  const needsSubscription = (currentPlan === "FREE" && !currentEndDate) || isExpired;
  const isStrictlyLocked = (needsSubscription || !hasPlanAccess) && !isEssentialRoute;

  if (isStrictlyLocked || !hasPlanAccess) {
    // If it's a mandatory redirect for a new user, send to subscriber page immediately
    if (needsSubscription && !requiredLevel && !isEssentialRoute) {
      const redirectPath = bussinessUuid 
        ? `/${bussinessUuid}/dashboard/subscriber` 
        : `/pricing`;
      return <Navigate to={redirectPath} replace />;
    }

    return (
      <div className='flex flex-col items-center justify-center min-h-[70vh] p-6 text-center animate-in fade-in zoom-in duration-300'>
        <div className='w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mb-6 border border-amber-500/20'>
          <FiLock className='text-4xl text-amber-500' />
        </div>
        <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-3'>
          Upgrade Your Plan
        </h2>
        <p className='text-gray-600 dark:text-gray-400 max-w-md mb-8'>
          This premium feature is not available in your current <strong>{currentPlan}</strong> plan. Upgrade today to unlock full access.
        </p>
        <div className="flex gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
          >
            <FiArrowLeft /> Go Back
          </button>
          <button 
            onClick={() => navigate('/pricing')}
            className="px-8 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02]"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return children;
  }

  if (isOwnerAdmin) {
    return children;
  }


  // Wait for allowedRoutes and permissions to be loaded before checking access
  if ((routeKey && (!allowedRoutes || allowedRoutes.length === 0)) ||
      (requiredPermissions && requiredPermissions.length > 0 && (!permissions || Object.keys(permissions).length === 0))) {
    // Show loading until data is available
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        {/* <div className="text-center">
          <div className="loader mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div> */}
         <LogoLoading />
      </div>
    );
  }

  let hasAccess = true;
  if (routeKey) {
    hasAccess = allowedRoutes.includes(routeKey);
  }
  if (requiredPermissions && requiredPermissions.length > 0 && permissions) {
    hasAccess = requiredPermissions.some((perm) => permissions[perm] === true);
  }


  if (!hasAccess) {
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }

    return (
      <div className='flex flex-col items-center justify-center min-h-[60vh]'>
        <div className='text-center'>
          <h2 className='text-2xl font-bold text-red-600 mb-4'>
            Access Denied
          </h2>
          <p className='text-gray-600 dark:text-gray-400'>
            You don't have permission to access this page.
          </p>
          <p className='text-gray-500 text-sm mt-2'>
            Please contact your administrator if you need access.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
